import React, { useState, useEffect } from "react";
import {
  UserIcon,
  CheckCircleIcon,
  MagnifyingGlassIcon,
  PencilSquareIcon,
  Squares2X2Icon,
  TrashIcon,
  UserPlusIcon,
} from "@heroicons/react/24/outline";
import { apiService } from "../services/apiService";
import { User, UserRole, Workflow } from "../types";
import toast from "../utils/toast";
import { useTranslation } from "react-i18next";
import { useAuth } from "../contexts/AuthContext";
import { ConfirmDialog } from "../components/common/ConfirmDialog";

type AccountFormState = {
  firstName: string;
  lastName: string;
  username: string;
  email: string;
  password: string;
  confirmPassword: string;
  role: UserRole;
};

export const TeamManagement: React.FC = () => {
  const { t } = useTranslation(["users", "common", "errors", "auth"]);
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState<number | null>(null);
  const [workflows, setWorkflows] = useState<Workflow[]>([]);
  const [showWorkflowModal, setShowWorkflowModal] = useState(false);
  const [selectedWorkflowIds, setSelectedWorkflowIds] = useState<number[]>([]);
  const [workflowSearch, setWorkflowSearch] = useState("");
  const [projectInstances, setProjectInstances] = useState<any[]>([]);
  const [loadingProjectInstances, setLoadingProjectInstances] = useState(false);
  const [showVisitorModal, setShowVisitorModal] = useState(false);
  const [selectedWorkflowInstanceIds, setSelectedWorkflowInstanceIds] =
    useState<number[]>([]);
  const [visitorFinancialAccess, setVisitorFinancialAccess] = useState(false);
  const [visitorProjectSearch, setVisitorProjectSearch] = useState("");
  const [targetUser, setTargetUser] = useState<User | null>(null);
  const [userToDelete, setUserToDelete] = useState<User | null>(null);
  const [userToPromote, setUserToPromote] = useState<User | null>(null);
  const [showAccountModal, setShowAccountModal] = useState(false);
  const [accountModalMode, setAccountModalMode] = useState<"create" | "edit">(
    "create"
  );
  const [accountForm, setAccountForm] = useState<AccountFormState>({
    firstName: "",
    lastName: "",
    username: "",
    email: "",
    password: "",
    confirmPassword: "",
    role: UserRole.INTERN,
  });

  useEffect(() => {
    loadUsers();
    loadWorkflows();
    loadWorkflowInstances();
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      // Include self to show current user in the list
      const data = await apiService.getUsers(true, true);
      setUsers(data);
    } catch (error) {
      toast.error(t("users:loadUsersFailed"));
    } finally {
      setLoading(false);
    }
  };

  const loadWorkflows = async () => {
    try {
      const data = await apiService.getWorkflows();
      setWorkflows(data || []);
    } catch (error) {
    }
  };

  const loadWorkflowInstances = async () => {
    try {
      setLoadingProjectInstances(true);
      const data = await apiService.getWorkflowInstances();
      setProjectInstances(Array.isArray(data) ? data : []);
    } catch (error) {
      setProjectInstances([]);
    } finally {
      setLoadingProjectInstances(false);
    }
  };

  const getUserAllowedWorkflowIds = (user: User): number[] => {
    if (!user.settingsJson) {
      return [];
    }
    try {
      const settings = JSON.parse(user.settingsJson);
      const rawIds = settings?.workflowPermissions?.allowedWorkflowIds;
      if (!Array.isArray(rawIds)) {
        return [];
      }
      return rawIds
        .map((id: any) => Number.parseInt(String(id), 10))
        .filter((id: number) => !Number.isNaN(id));
    } catch (error) {
      return [];
    }
  };

  const getUserAllowedWorkflowInstanceIds = (user: User): number[] => {
    if (!user.settingsJson) {
      return [];
    }
    try {
      const settings = JSON.parse(user.settingsJson);
      const rawIds =
        settings?.workflowPermissions?.visitorAllowedWorkflowInstanceIds;
      if (!Array.isArray(rawIds)) {
        return [];
      }
      return rawIds
        .map((id: any) => Number.parseInt(String(id), 10))
        .filter((id: number) => !Number.isNaN(id));
    } catch (error) {
      return [];
    }
  };

  const getUserVisitorFinancialAccess = (user: User): boolean => {
    if (!user.settingsJson) {
      return false;
    }
    try {
      const settings = JSON.parse(user.settingsJson);
      return Boolean(settings?.workflowPermissions?.visitorFinancialAccess);
    } catch (error) {
      return false;
    }
  };

  const updateUserInList = (userId: number, updater: (user: User) => User) => {
    setUsers((prev) =>
      prev.map((item) => (item.id === userId ? updater(item) : item))
    );
  };

  const buildSettingsJsonWithWorkflows = (
    user: User,
    workflowIds: number[]
  ): string => {
    try {
      const settings = user.settingsJson ? JSON.parse(user.settingsJson) : {};
      const next = {
        ...settings,
        workflowPermissions: {
          ...(settings?.workflowPermissions || {}),
          allowedWorkflowIds: workflowIds,
        },
      };
      return JSON.stringify(next);
    } catch (error) {
      return JSON.stringify({
        workflowPermissions: { allowedWorkflowIds: workflowIds },
      });
    }
  };

  const openWorkflowModal = (user: User) => {
    setTargetUser(user);
    setSelectedWorkflowIds(getUserAllowedWorkflowIds(user));
    setWorkflowSearch("");
    setShowWorkflowModal(true);
  };

  const openVisitorModal = (user: User) => {
    setTargetUser(user);
    setSelectedWorkflowInstanceIds(getUserAllowedWorkflowInstanceIds(user));
    setVisitorFinancialAccess(getUserVisitorFinancialAccess(user));
    setVisitorProjectSearch("");
    setShowVisitorModal(true);
  };

  const closeWorkflowModal = () => {
    setShowWorkflowModal(false);
    setTargetUser(null);
    setSelectedWorkflowIds([]);
    setWorkflowSearch("");
  };

  const closeVisitorModal = () => {
    setShowVisitorModal(false);
    setTargetUser(null);
    setSelectedWorkflowInstanceIds([]);
    setVisitorFinancialAccess(false);
    setVisitorProjectSearch("");
  };

  const openCreateAccountModal = () => {
    setAccountModalMode("create");
    setTargetUser(null);
    setSelectedWorkflowIds([]);
    setWorkflowSearch("");
    setSelectedWorkflowInstanceIds([]);
    setVisitorFinancialAccess(false);
    setVisitorProjectSearch("");
    setAccountForm({
      firstName: "",
      lastName: "",
      username: "",
      email: "",
      password: "",
      confirmPassword: "",
      role: UserRole.INTERN,
    });
    setShowAccountModal(true);
  };

  const openEditAccountModal = (user: User) => {
    setAccountModalMode("edit");
    setTargetUser(user);
    setAccountForm({
      firstName: user.firstName || "",
      lastName: user.lastName || "",
      username: user.username || "",
      email: user.email || "",
      password: "",
      confirmPassword: "",
      role: user.role,
    });
    setShowAccountModal(true);
  };

  const closeAccountModal = () => {
    setShowAccountModal(false);
    setTargetUser(null);
    setSelectedWorkflowIds([]);
    setWorkflowSearch("");
    setSelectedWorkflowInstanceIds([]);
    setVisitorFinancialAccess(false);
    setVisitorProjectSearch("");
    setAccountForm({
      firstName: "",
      lastName: "",
      username: "",
      email: "",
      password: "",
      confirmPassword: "",
      role: UserRole.INTERN,
    });
  };

  const toggleWorkflowSelection = (workflowId: number) => {
    setSelectedWorkflowIds((prev) => {
      if (prev.includes(workflowId)) {
        return prev.filter((id) => id !== workflowId);
      }
      return [...prev, workflowId];
    });
  };

  const toggleWorkflowInstanceSelection = (instanceId: number) => {
    setSelectedWorkflowInstanceIds((prev) => {
      if (prev.includes(instanceId)) {
        return prev.filter((id) => id !== instanceId);
      }
      return [...prev, instanceId];
    });
  };

  const normalizedWorkflowSearch = workflowSearch.trim().toLowerCase();
  const filteredWorkflows = workflows
    .filter((workflow) => {
      if (!normalizedWorkflowSearch) return true;
      return workflow.name?.toLowerCase().includes(normalizedWorkflowSearch);
    })
    .slice()
    .sort((a, b) => (a.name || "").localeCompare(b.name || ""));

  const normalizedVisitorProjectSearch = visitorProjectSearch
    .trim()
    .toLowerCase();

  const getProjectInstanceSortValue = (instance: any): number => {
    const timestampCandidates = [instance?.createdAt, instance?.startedAt];
    for (const candidate of timestampCandidates) {
      if (!candidate) {
        continue;
      }
      const parsed = Date.parse(String(candidate));
      if (!Number.isNaN(parsed)) {
        return parsed;
      }
    }

    const numericId = Number(instance?.id);
    return Number.isNaN(numericId) ? 0 : numericId;
  };

  const filteredProjectInstances = projectInstances
    .filter((instance: any) => {
      if (!normalizedVisitorProjectSearch) return true;
      const label =
        String(
          instance?.workflowContainerName ??
            instance?.name ??
            instance?.id ??
            ""
        ).toLowerCase() || "";
      return label.includes(normalizedVisitorProjectSearch);
    })
    .slice()
    .sort((a: any, b: any) => {
      const sortDifference =
        getProjectInstanceSortValue(b) - getProjectInstanceSortValue(a);
      if (sortDifference !== 0) {
        return sortDifference;
      }

      const idDifference = Number(b?.id ?? 0) - Number(a?.id ?? 0);
      if (idDifference !== 0) {
        return idDifference;
      }

      return String(a?.workflowContainerName ?? a?.name ?? "")
        .toLowerCase()
        .localeCompare(
          String(b?.workflowContainerName ?? b?.name ?? "").toLowerCase()
        );
    });

  const updateUserRole = async (userId: number, newRole: UserRole) => {
    try {
      setUpdating(userId);
      await apiService.updateUser(userId, { role: newRole });
      toast.success(t("users:roleUpdated"));
      updateUserInList(userId, (item) => ({ ...item, role: newRole }));
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message || t("users:roleUpdateFailed");
      toast.error(errorMessage);
    } finally {
      setUpdating(null);
    }
  };

  const handleRoleChange = async (userId: number, newRole: UserRole) => {
    try {
      const target = users.find((item) => item.id === userId);
      if (!target) {
        return;
      }

      if (newRole === UserRole.PROJECT_MANAGER) {
        openWorkflowModal(target);
        return;
      }
      if (newRole === UserRole.VISITOR) {
        openVisitorModal(target);
        return;
      }
      if (newRole === UserRole.MANAGER && currentUser?.role === UserRole.MANAGER) {
        setUserToPromote(target);
        return;
      }

      await updateUserRole(userId, newRole);
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || t("users:roleUpdateFailed");
      toast.error(errorMessage);
    }
  };

  const handleWorkflowSelectionSave = async () => {
    if (!targetUser) {
      return;
    }
    if (selectedWorkflowIds.length === 0) {
      toast.error(t("users:selectAtLeastOneWorkflow"));
      return;
    }
    try {
      setUpdating(targetUser.id ?? null);
      await apiService.updateUser(targetUser.id!, {
        role: UserRole.PROJECT_MANAGER,
        allowedWorkflowIds: selectedWorkflowIds,
      });
      toast.success(t("users:roleUpdated"));
      updateUserInList(targetUser.id!, (item) => ({
        ...item,
        role: UserRole.PROJECT_MANAGER,
        settingsJson: buildSettingsJsonWithWorkflows(item, selectedWorkflowIds),
      }));
      closeWorkflowModal();
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message || t("users:roleUpdateFailed");
      toast.error(errorMessage);
    } finally {
      setUpdating(null);
    }
  };

  const handleVisitorSelectionSave = async () => {
    if (!targetUser) {
      return;
    }
    if (selectedWorkflowInstanceIds.length === 0) {
      toast.error(
        t("users:selectAtLeastOneProject", "En az bir proje seçmelisiniz")
      );
      return;
    }
    try {
      setUpdating(targetUser.id ?? null);
      await apiService.updateUser(targetUser.id!, {
        role: UserRole.VISITOR,
        allowedWorkflowInstanceIds: selectedWorkflowInstanceIds,
        visitorFinancialAccess,
      });
      toast.success(t("users:roleUpdated"));
      await loadUsers();
      closeVisitorModal();
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message || t("users:roleUpdateFailed");
      toast.error(errorMessage);
    } finally {
      setUpdating(null);
    }
  };

  const handleAccountSave = async (event: React.FormEvent) => {
    event.preventDefault();

    const firstName = accountForm.firstName.trim();
    const lastName = accountForm.lastName.trim();
    const username = accountForm.username.trim();
    const email = accountForm.email.trim();
    const password = accountForm.password.trim();
    const confirmPassword = accountForm.confirmPassword.trim();

    if (!firstName || !lastName || !username || !email) {
      toast.error(
        t("errors:requiredField", "Ad, soyad, kullanıcı adı ve e-posta gerekli")
      );
      return;
    }
    if (firstName.length < 2 || lastName.length < 2) {
      toast.error(t("errors:minLength", { min: 2 }));
      return;
    }
    if (username.length < 3) {
      toast.error(t("errors:minLength", { min: 3 }));
      return;
    }
    if (!/^[a-zA-Z0-9_.çÇğĞıİöÖşŞüÜ]+$/.test(username)) {
      toast.error(t("auth:usernamePattern"));
      return;
    }
    if (accountModalMode === "create" && !password) {
      toast.error(t("auth:passwordRequired", "Şifre gerekli"));
      return;
    }
    if (password && password.length < 6) {
      toast.error(t("errors:minLength", { min: 6 }));
      return;
    }
    if (password && password !== confirmPassword) {
      toast.error(t("auth:passwordsMustMatch", "Şifreler eşleşmiyor"));
      return;
    }
    if (
      accountModalMode === "create" &&
      accountForm.role === UserRole.PROJECT_MANAGER &&
      selectedWorkflowIds.length === 0
    ) {
      toast.error(t("users:selectAtLeastOneWorkflow"));
      return;
    }
    if (
      accountModalMode === "create" &&
      accountForm.role === UserRole.VISITOR &&
      selectedWorkflowInstanceIds.length === 0
    ) {
      toast.error(
        t("users:selectAtLeastOneProject", "En az bir proje seçmelisiniz")
      );
      return;
    }

    try {
      setUpdating(targetUser?.id ?? 0);

      if (accountModalMode === "create") {
        await apiService.createUser({
          firstName,
          lastName,
          username,
          email,
          password,
          role: accountForm.role,
          ...(accountForm.role === UserRole.PROJECT_MANAGER
            ? { allowedWorkflowIds: selectedWorkflowIds }
            : {}),
          ...(accountForm.role === UserRole.VISITOR
            ? {
                allowedWorkflowInstanceIds: selectedWorkflowInstanceIds,
                visitorFinancialAccess,
              }
            : {}),
        });
        toast.success(t("users:userCreated"));
      } else if (targetUser?.id) {
        await apiService.updateUser(targetUser.id, {
          firstName,
          lastName,
          username,
          email,
          ...(password ? { password } : {}),
        });
        toast.success(t("users:userUpdated"));
      }

      closeAccountModal();
      await loadUsers();
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message ||
          error?.message ||
          t("users:saveUserFailed")
      );
    } finally {
      setUpdating(null);
    }
  };

  const confirmDeleteUser = async () => {
    if (!userToDelete?.id) {
      return;
    }

    try {
      setUpdating(userToDelete.id);
      await apiService.deleteUser(userToDelete.id);
      toast.success(t("users:userDeleted"));
      await loadUsers();
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message ||
          error?.message ||
          t("users:deleteUserFailed")
      );
    } finally {
      setUpdating(null);
      setUserToDelete(null);
    }
  };

  const confirmPromoteToManager = async () => {
    if (!userToPromote?.id) {
      return;
    }

    const promotedUserId = userToPromote.id;
    setUserToPromote(null);
    await updateUserRole(promotedUserId, UserRole.MANAGER);
  };

  const canChangeRole = (user: User): boolean => {
    // Cannot change own role
    if (user.id === currentUser?.id) {
      return false;
    }
    // Cannot change admin roles
    if (user.role === UserRole.ADMIN) {
      return false;
    }
    if (currentUser?.role === UserRole.MANAGER && user.role === UserRole.MANAGER) {
      return false;
    }
    return true;
  };

  const getRoleChangeBlockedMessage = (user: User): string => {
    if (user.id === currentUser?.id) {
      return t("users:cannotModifySelf");
    }
    if (user.role === UserRole.ADMIN) {
      return t("users:cannotModifyAdmin");
    }
    if (user.role === UserRole.MANAGER) {
      return t(
        "users:cannotModifyManager",
        "Yöneticinin rolünü değiştiremezsiniz"
      );
    }
    return t("users:cannotModifyRole", "Bu kullanıcının rolü değiştirilemez");
  };

  const canManageAccount = (user: User): boolean => {
    if (user.id === currentUser?.id) {
      return false;
    }
    if (currentUser?.role === UserRole.ADMIN) {
      return true;
    }
    return user.role !== UserRole.ADMIN && user.role !== UserRole.MANAGER;
  };

  const canDeleteAccount = (user: User): boolean => canManageAccount(user);

  const getRoleDisplayName = (role: UserRole): string => {
    switch (role) {
      case UserRole.ADMIN:
        return t("users:roles.admin");
      case UserRole.MANAGER:
        return t("users:roles.manager");
      case UserRole.PROJECT_MANAGER:
        return t("users:roles.projectManager");
      case UserRole.INTERN:
        return t("users:roles.intern");
      case UserRole.EMPLOYEE:
        return t("users:roles.employee");
      case UserRole.VISITOR:
        return t("users:roles.visitor");
      default:
        return role;
    }
  };

  const getRoleBadgeColor = (role: UserRole): string => {
    switch (role) {
      case UserRole.ADMIN:
        return "bg-purple-100 text-purple-800";
      case UserRole.MANAGER:
        return "bg-green-100 text-green-800";
      case UserRole.PROJECT_MANAGER:
        return "bg-indigo-100 text-indigo-800";
      case UserRole.INTERN:
        return "bg-amber-100 text-amber-800";
      case UserRole.EMPLOYEE:
        return "bg-blue-100 text-blue-800";
      case UserRole.VISITOR:
        return "bg-slate-100 text-slate-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const workflowNameById = (id: number): string => {
    const workflow = workflows.find((item) => item.id === id);
    return workflow?.name || String(id);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <div className="flex flex-col items-center gap-3 text-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              {currentUser?.company?.name && (
                <span className="text-blue-600">
                  {currentUser.company.name} -{" "}
                </span>
              )}
              {t("users:teamManagement")}
            </h1>
            <p className="mt-2 text-sm text-gray-600">
              {t("users:teamManagementDescription")}
            </p>
          </div>
          <button
            type="button"
            onClick={openCreateAccountModal}
            className="inline-flex items-center px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 transition-colors shadow-sm"
          >
            <UserPlusIcon className="w-4 h-4 mr-2" />
            {t("users:createUser")}
          </button>
        </div>
      </div>

      <div className="bg-white shadow overflow-x-auto sm:rounded-lg">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                {t("users:user")}
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                {t("users:email")}
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                {t("users:currentRole")}
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                {t("users:actions")}
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {users.map((user) => (
              <tr
                key={user.id}
                className={user.id === currentUser?.id ? "bg-blue-50" : ""}
              >
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <UserIcon className="h-10 w-10 text-gray-400" />
                    </div>
                    <div className="ml-4">
                      <div className="flex items-center">
                        <div className="text-sm font-medium text-gray-900">
                          {user.firstName && user.lastName
                            ? `${user.firstName} ${user.lastName}`
                            : user.username}
                        </div>
                        {user.id === currentUser?.id && (
                          <span className="ml-2 text-xs text-blue-600 font-medium">
                            ({t("users:you")})
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-gray-500">
                        @{user.username}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">{user.email}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span
                    className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getRoleBadgeColor(
                      user.role
                    )}`}
                  >
                    {getRoleDisplayName(user.role)}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  {canChangeRole(user) ? (
                    <div className="flex items-center gap-2">
                      <select
                        value={user.role}
                        onChange={(e) =>
                          handleRoleChange(user.id!, e.target.value as UserRole)
                        }
                        disabled={updating === user.id}
                        className="block w-40 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <option value={UserRole.EMPLOYEE}>
                          {t("users:roles.employee")}
                        </option>
                        <option value={UserRole.VISITOR}>
                          {t("users:roles.visitor")}
                        </option>
                        <option value={UserRole.PROJECT_MANAGER}>
                          {t("users:roles.projectManager")}
                        </option>
                        <option value={UserRole.INTERN}>
                          {t("users:roles.intern")}
                        </option>
                        <option value={UserRole.MANAGER}>
                          {t("users:roles.manager")}
                        </option>
                      </select>
                      {user.role === UserRole.PROJECT_MANAGER && (
                        <button
                          type="button"
                          onClick={() => openWorkflowModal(user)}
                          className="inline-flex max-w-full items-center gap-1 text-xs text-blue-700 hover:text-blue-800 border border-blue-200 rounded-md px-2 py-1 hover:bg-blue-50"
                        >
                          <Squares2X2Icon className="h-4 w-4 flex-shrink-0" />
                          <span className="truncate">
                            {t("users:editProjectManagerWorkflows")}
                          </span>
                        </button>
                      )}
                      {user.role === UserRole.VISITOR && (
                        <button
                          type="button"
                          onClick={() => openVisitorModal(user)}
                          className="inline-flex max-w-full items-center gap-1 text-xs text-blue-700 hover:text-blue-800 border border-blue-200 rounded-md px-2 py-1 hover:bg-blue-50"
                        >
                          <Squares2X2Icon className="h-4 w-4 flex-shrink-0" />
                          <span className="truncate">
                            {t(
                              "users:editVisitorAccess",
                              "Ziyaretçi erişimini düzenle"
                            )}
                          </span>
                        </button>
                      )}
                      {canManageAccount(user) && (
                        <button
                          type="button"
                          onClick={() => openEditAccountModal(user)}
                          className="inline-flex items-center gap-1 text-xs text-slate-700 hover:text-slate-900 border border-slate-200 rounded-md px-2 py-1 hover:bg-slate-50"
                        >
                          <PencilSquareIcon className="h-4 w-4 flex-shrink-0" />
                          {t("users:account", "Hesap")}
                        </button>
                      )}
                      {canDeleteAccount(user) && (
                        <button
                          type="button"
                          onClick={() => setUserToDelete(user)}
                          className="inline-flex items-center gap-1 text-xs text-red-700 hover:text-red-800 border border-red-200 rounded-md px-2 py-1 hover:bg-red-50"
                        >
                          <TrashIcon className="h-4 w-4 flex-shrink-0" />
                          {t("common:delete", "Sil")}
                        </button>
                      )}
                      {updating === user.id && (
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                      )}
                    </div>
                  ) : (
                    <span className="text-gray-400 text-sm">
                      {getRoleChangeBlockedMessage(user)}
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {users.length === 0 && (
        <div className="text-center py-12">
          <UserIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">
            {t("users:noUsers")}
          </h3>
        </div>
      )}

      {showAccountModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-6 border-b border-slate-200 bg-slate-50">
              <h3 className="text-lg font-semibold text-slate-900">
                {accountModalMode === "create"
                  ? t("users:createUser")
                  : t("users:editAccount", "Hesap Bilgileri")}
              </h3>
              <p className="text-sm text-slate-600 mt-1">
                {accountModalMode === "create"
                  ? t(
                      "users:createUserDescription",
                      "Yeni çalışan veya stajyer için kullanıcı adı ve şifre oluşturun."
                    )
                  : t(
                      "users:editAccountDescription",
                      "Kullanıcı adı, e-posta veya şifre bilgisini güncelleyin."
                    )}
              </p>
            </div>

            <form onSubmit={handleAccountSave} className="p-6 space-y-4 overflow-y-auto">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    {t("auth:firstName")}
                  </label>
                  <input
                    type="text"
                    value={accountForm.firstName}
                    onChange={(event) =>
                      setAccountForm((prev) => ({
                        ...prev,
                        firstName: event.target.value,
                      }))
                    }
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    {t("auth:lastName")}
                  </label>
                  <input
                    type="text"
                    value={accountForm.lastName}
                    onChange={(event) =>
                      setAccountForm((prev) => ({
                        ...prev,
                        lastName: event.target.value,
                      }))
                    }
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  {t("auth:username")}
                </label>
                <input
                  type="text"
                  value={accountForm.username}
                  onChange={(event) =>
                    setAccountForm((prev) => ({
                      ...prev,
                      username: event.target.value,
                    }))
                  }
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  {t("auth:email")}
                </label>
                <input
                  type="email"
                  value={accountForm.email}
                  onChange={(event) =>
                    setAccountForm((prev) => ({
                      ...prev,
                      email: event.target.value,
                    }))
                  }
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  {t("auth:password")}{" "}
                  {accountModalMode === "edit" && (
                    <span className="text-slate-400">
                      ({t("common:optional")})
                    </span>
                  )}
                </label>
                <input
                  type="password"
                  value={accountForm.password}
                  onChange={(event) =>
                    setAccountForm((prev) => ({
                      ...prev,
                      password: event.target.value,
                    }))
                  }
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  required={accountModalMode === "create"}
                />
                {accountModalMode === "edit" && (
                  <p className="mt-1 text-xs text-slate-500">
                    {t(
                      "users:leavePasswordBlank",
                      "Boş bırakırsanız mevcut şifre değişmez."
                    )}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  {t("auth:confirmPassword")}{" "}
                  {accountModalMode === "edit" && (
                    <span className="text-slate-400">
                      ({t("common:optional")})
                    </span>
                  )}
                </label>
                <input
                  type="password"
                  value={accountForm.confirmPassword}
                  onChange={(event) =>
                    setAccountForm((prev) => ({
                      ...prev,
                      confirmPassword: event.target.value,
                    }))
                  }
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  required={accountModalMode === "create"}
                />
              </div>

              {accountModalMode === "create" && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    {t("auth:role")}
                  </label>
                  <select
                    value={accountForm.role}
                    onChange={(event) =>
                      setAccountForm((prev) => ({
                        ...prev,
                        role: event.target.value as UserRole,
                      }))
                    }
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  >
                    <option value={UserRole.INTERN}>
                      {t("users:roles.intern")}
                    </option>
                    <option value={UserRole.EMPLOYEE}>
                      {t("users:roles.employee")}
                    </option>
                    <option value={UserRole.PROJECT_MANAGER}>
                      {t("users:roles.projectManager")}
                    </option>
                    <option value={UserRole.VISITOR}>
                      {t("users:roles.visitor")}
                    </option>
                    <option value={UserRole.MANAGER}>
                      {t("users:roles.manager")}
                    </option>
                    {currentUser?.role === UserRole.ADMIN && (
                      <option value={UserRole.ADMIN}>
                        {t("users:roles.admin")}
                      </option>
                    )}
                  </select>
                </div>
              )}

              {accountModalMode === "create" &&
                accountForm.role === UserRole.PROJECT_MANAGER && (
                  <div className="rounded-xl border border-blue-100 bg-blue-50/50 p-4 space-y-3">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <h4 className="text-sm font-semibold text-slate-900">
                          {t("users:projectManagerWorkflows")}
                        </h4>
                        <p className="text-xs text-slate-600 mt-1">
                          {t("users:projectManagerWorkflowsDescription")}
                        </p>
                      </div>
                      <span className="text-xs font-medium text-blue-700 bg-blue-100 px-2 py-1 rounded-full">
                        {t("common:selected")}: {selectedWorkflowIds.length}
                      </span>
                    </div>
                    <div className="relative">
                      <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                      <input
                        type="text"
                        value={workflowSearch}
                        onChange={(event) =>
                          setWorkflowSearch(event.target.value)
                        }
                        placeholder={t("common:searchPlaceholder")}
                        className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() =>
                          setSelectedWorkflowIds(
                            filteredWorkflows.map((workflow) => workflow.id)
                          )
                        }
                        className="px-3 py-2 text-xs font-medium border border-slate-200 rounded-lg text-slate-600 hover:bg-white"
                      >
                        {t("common:selectAll")}
                      </button>
                      <button
                        type="button"
                        onClick={() => setSelectedWorkflowIds([])}
                        className="px-3 py-2 text-xs font-medium border border-slate-200 rounded-lg text-slate-600 hover:bg-white"
                      >
                        {t("common:clear")}
                      </button>
                    </div>
                    <div className="grid grid-cols-1 gap-2 max-h-56 overflow-y-auto">
                      {workflows.length === 0 ? (
                        <p className="text-sm text-slate-500">
                          {t("users:noWorkflowsFound")}
                        </p>
                      ) : filteredWorkflows.length === 0 ? (
                        <p className="text-sm text-slate-500">
                          {t("common:noResults")}
                        </p>
                      ) : (
                        filteredWorkflows.map((workflow) => {
                          const isSelected = selectedWorkflowIds.includes(
                            workflow.id
                          );
                          return (
                            <button
                              key={workflow.id}
                              type="button"
                              onClick={() =>
                                toggleWorkflowSelection(workflow.id)
                              }
                              className={`flex items-center gap-3 border rounded-lg px-3 py-2 text-left transition-colors ${
                                isSelected
                                  ? "border-blue-300 bg-white"
                                  : "border-slate-200 hover:bg-white"
                              }`}
                            >
                              <CheckCircleIcon
                                className={`h-4 w-4 ${
                                  isSelected
                                    ? "text-blue-600"
                                    : "text-slate-300"
                                }`}
                              />
                              <span className="text-sm font-medium text-slate-800 truncate">
                                {workflow.name || workflow.id}
                              </span>
                            </button>
                          );
                        })
                      )}
                    </div>
                  </div>
                )}

              {accountModalMode === "create" &&
                accountForm.role === UserRole.VISITOR && (
                  <div className="rounded-xl border border-sky-100 bg-sky-50/50 p-4 space-y-3">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <h4 className="text-sm font-semibold text-slate-900">
                          {t("users:visitorProjects", "Projeler")}
                        </h4>
                        <p className="text-xs text-slate-600 mt-1">
                          {t(
                            "users:visitorProjectsDescription",
                            "Ziyaretçinin görebileceği projeleri seçin"
                          )}
                        </p>
                      </div>
                      <span className="text-xs font-medium text-sky-700 bg-sky-100 px-2 py-1 rounded-full">
                        {t("common:selected")}:{" "}
                        {selectedWorkflowInstanceIds.length}
                      </span>
                    </div>
                    <div className="relative">
                      <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                      <input
                        type="text"
                        value={visitorProjectSearch}
                        onChange={(event) =>
                          setVisitorProjectSearch(event.target.value)
                        }
                        placeholder={t("common:searchPlaceholder")}
                        className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() =>
                          setSelectedWorkflowInstanceIds(
                            filteredProjectInstances
                              .map((instance: any) => Number(instance?.id))
                              .filter((id: number) => !Number.isNaN(id))
                          )
                        }
                        className="px-3 py-2 text-xs font-medium border border-slate-200 rounded-lg text-slate-600 hover:bg-white"
                      >
                        {t("common:selectAll")}
                      </button>
                      <button
                        type="button"
                        onClick={() => setSelectedWorkflowInstanceIds([])}
                        className="px-3 py-2 text-xs font-medium border border-slate-200 rounded-lg text-slate-600 hover:bg-white"
                      >
                        {t("common:clear")}
                      </button>
                    </div>
                    <div className="grid grid-cols-1 gap-2 max-h-56 overflow-y-auto">
                      {loadingProjectInstances ? (
                        <p className="text-sm text-slate-500">
                          {t("common:loading")}
                        </p>
                      ) : projectInstances.length === 0 ? (
                        <p className="text-sm text-slate-500">
                          {t("users:noProjectsFound", "Proje bulunamadı")}
                        </p>
                      ) : filteredProjectInstances.length === 0 ? (
                        <p className="text-sm text-slate-500">
                          {t("common:noResults")}
                        </p>
                      ) : (
                        filteredProjectInstances.map((instance: any) => {
                          const instanceId = Number(instance?.id);
                          const label =
                            instance?.workflowContainerName ||
                            instance?.name ||
                            String(instance?.id ?? "-");
                          const isSelected =
                            !Number.isNaN(instanceId) &&
                            selectedWorkflowInstanceIds.includes(instanceId);
                          return (
                            <button
                              key={String(instance?.id)}
                              type="button"
                              onClick={() => {
                                if (!Number.isNaN(instanceId)) {
                                  toggleWorkflowInstanceSelection(instanceId);
                                }
                              }}
                              disabled={Number.isNaN(instanceId)}
                              className={`flex items-center gap-3 border rounded-lg px-3 py-2 text-left transition-colors ${
                                isSelected
                                  ? "border-blue-300 bg-white"
                                  : "border-slate-200 hover:bg-white"
                              }`}
                            >
                              <CheckCircleIcon
                                className={`h-4 w-4 ${
                                  isSelected
                                    ? "text-blue-600"
                                    : "text-slate-300"
                                }`}
                              />
                              <span className="text-sm font-medium text-slate-800 truncate">
                                {label}
                              </span>
                            </button>
                          );
                        })
                      )}
                    </div>
                    <label className="flex items-start gap-3 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700">
                      <input
                        type="checkbox"
                        checked={visitorFinancialAccess}
                        onChange={(event) =>
                          setVisitorFinancialAccess(event.target.checked)
                        }
                        className="mt-1 h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span>
                        <span className="font-semibold text-slate-900">
                          {t(
                            "users:visitorFinancialAccess",
                            "Fatura ve teklif bilgilerini görüntüleyebilir"
                          )}
                        </span>
                        <br />
                        <span className="text-xs text-slate-500">
                          {t(
                            "users:visitorFinancialAccessDescription",
                            "İşaretlenirse ziyaretçi finansal proje bilgilerini görebilir."
                          )}
                        </span>
                      </span>
                    </label>
                  </div>
                )}

              <div className="pt-2 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={closeAccountModal}
                  className="px-4 py-2 text-sm border border-slate-200 rounded-lg text-slate-700 hover:bg-slate-50"
                >
                  {t("common:cancel")}
                </button>
                <button
                  type="submit"
                  disabled={updating !== null}
                  className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-60"
                >
                  {updating !== null
                    ? t("common:loading")
                    : accountModalMode === "create"
                      ? t("common:create")
                      : t("common:save")}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showWorkflowModal && targetUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-3xl overflow-hidden">
            <div className="p-6 border-b border-slate-200 bg-gradient-to-r from-indigo-50 via-blue-50 to-sky-50">
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-3">
                  <div className="h-10 w-10 rounded-xl bg-white/80 border border-slate-200 flex items-center justify-center text-slate-600">
                    <Squares2X2Icon className="h-5 w-5" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-slate-900">
                      {t("users:projectManagerWorkflows")}
                    </h3>
                    <p className="text-sm text-slate-600 mt-1">
                      {t("users:projectManagerWorkflowsDescription")}
                    </p>
                  </div>
                </div>
                <span className="text-xs font-medium text-indigo-700 bg-indigo-100 px-2 py-1 rounded-full">
                  {t("common:selected")}: {selectedWorkflowIds.length}
                </span>
              </div>
            </div>

            <div className="p-6">
              <div className="flex flex-col lg:flex-row lg:items-center gap-3 mb-5">
                <div className="relative flex-1">
                  <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <input
                    type="text"
                    value={workflowSearch}
                    onChange={(e) => setWorkflowSearch(e.target.value)}
                    placeholder={t("common:searchPlaceholder")}
                    className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() =>
                      setSelectedWorkflowIds(filteredWorkflows.map((w) => w.id))
                    }
                    className="px-3 py-2 text-xs font-medium border border-slate-200 rounded-lg text-slate-600 hover:bg-slate-50"
                  >
                    {t("common:selectAll")}
                  </button>
                  <button
                    type="button"
                    onClick={() => setSelectedWorkflowIds([])}
                    className="px-3 py-2 text-xs font-medium border border-slate-200 rounded-lg text-slate-600 hover:bg-slate-50"
                  >
                    {t("common:clear")}
                  </button>
                </div>
              </div>

              <div className="max-h-[22rem] overflow-y-auto">
                {workflows.length === 0 ? (
                  <p className="text-sm text-slate-500">
                    {t("users:noWorkflowsFound")}
                  </p>
                ) : filteredWorkflows.length === 0 ? (
                  <p className="text-sm text-slate-500">
                    {t("common:noResults")}
                  </p>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {filteredWorkflows.map((workflow) => {
                      const isSelected = selectedWorkflowIds.includes(
                        workflow.id
                      );
                      return (
                        <button
                          key={workflow.id}
                          type="button"
                          onClick={() => toggleWorkflowSelection(workflow.id)}
                          className={`flex items-center gap-3 border rounded-xl px-4 py-3 text-left transition-colors ${
                            isSelected
                              ? "border-blue-300 bg-blue-50"
                              : "border-slate-200 hover:bg-slate-50"
                          }`}
                        >
                          <div className="relative">
                            <div className="h-10 w-10 rounded-full bg-slate-100 flex items-center justify-center text-slate-500 text-xs font-semibold">
                              {(workflow.name || "?")
                                .trim()
                                .slice(0, 2)
                                .toUpperCase()}
                            </div>
                            {isSelected && (
                              <CheckCircleIcon className="h-4 w-4 text-blue-600 absolute -right-1 -bottom-1 bg-white rounded-full" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-semibold text-slate-900 truncate">
                              {workflow.name}
                            </div>
                            <div className="text-xs text-slate-500 truncate">
                              #{workflow.id}
                            </div>
                          </div>
                          <input
                            type="checkbox"
                            checked={isSelected}
                            readOnly
                            onClick={(event) => {
                              event.stopPropagation();
                              toggleWorkflowSelection(workflow.id);
                            }}
                            className="h-4 w-4 text-blue-600 border-slate-300 rounded"
                          />
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              {selectedWorkflowIds.length > 0 && (
                <div className="mt-5 flex flex-wrap gap-2">
                  {selectedWorkflowIds
                    .map((id) => ({ id, name: workflowNameById(id) }))
                    .filter((w) => w.name && w.name !== String(w.id))
                    .slice(0, 12)
                    .map((workflow) => (
                      <span
                        key={workflow.id}
                        className="text-xs px-2 py-1 rounded-full bg-slate-100 text-slate-700 border border-slate-200"
                      >
                        {workflow.name}
                      </span>
                    ))}
                  {selectedWorkflowIds.length > 12 && (
                    <span className="text-xs px-2 py-1 rounded-full bg-slate-50 text-slate-500 border border-slate-200">
                      +{selectedWorkflowIds.length - 12}
                    </span>
                  )}
                </div>
              )}
            </div>

            <div className="p-6 border-t border-slate-200 flex justify-end gap-3 bg-white">
              <button
                type="button"
                onClick={closeWorkflowModal}
                className="px-4 py-2 text-sm border border-slate-200 rounded-lg text-slate-700 hover:bg-slate-50"
              >
                {t("common:cancel")}
              </button>
              <button
                type="button"
                onClick={handleWorkflowSelectionSave}
                className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                {t("common:save")}
              </button>
            </div>
          </div>
        </div>
      )}
      {showVisitorModal && targetUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-3xl overflow-hidden">
            <div className="p-6 border-b border-slate-200 bg-gradient-to-r from-slate-50 via-blue-50 to-sky-50">
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-3">
                  <div className="h-10 w-10 rounded-xl bg-white/80 border border-slate-200 flex items-center justify-center text-slate-600">
                    <Squares2X2Icon className="h-5 w-5" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-slate-900">
                      {t("users:visitorProjects", "Projeler")}
                    </h3>
                    <p className="text-sm text-slate-600 mt-1">
                      {t(
                        "users:visitorProjectsDescription",
                        "Ziyaretçinin görebileceği projeleri seçin"
                      )}
                    </p>
                  </div>
                </div>
                <span className="text-xs font-medium text-sky-700 bg-sky-100 px-2 py-1 rounded-full">
                  {t("common:selected")}: {selectedWorkflowInstanceIds.length}
                </span>
              </div>
            </div>

            <div className="p-6">
              <div className="flex flex-col lg:flex-row lg:items-center gap-3 mb-5">
                <div className="relative flex-1">
                  <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <input
                    type="text"
                    value={visitorProjectSearch}
                    onChange={(e) => setVisitorProjectSearch(e.target.value)}
                    placeholder={t("common:searchPlaceholder")}
                    className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() =>
                      setSelectedWorkflowInstanceIds(
                        filteredProjectInstances
                          .map((instance: any) => Number(instance?.id))
                          .filter((id: number) => !Number.isNaN(id))
                      )
                    }
                    className="px-3 py-2 text-xs font-medium border border-slate-200 rounded-lg text-slate-600 hover:bg-slate-50"
                  >
                    {t("common:selectAll")}
                  </button>
                  <button
                    type="button"
                    onClick={() => setSelectedWorkflowInstanceIds([])}
                    className="px-3 py-2 text-xs font-medium border border-slate-200 rounded-lg text-slate-600 hover:bg-slate-50"
                  >
                    {t("common:clear")}
                  </button>
                </div>
              </div>

              <div className="max-h-[22rem] overflow-y-auto">
                {loadingProjectInstances ? (
                  <p className="text-sm text-slate-500">{t("common:loading")}</p>
                ) : projectInstances.length === 0 ? (
                  <p className="text-sm text-slate-500">
                    {t("users:noProjectsFound", "Proje bulunamadı")}
                  </p>
                ) : filteredProjectInstances.length === 0 ? (
                  <p className="text-sm text-slate-500">
                    {t("common:noResults")}
                  </p>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {filteredProjectInstances.map((instance: any) => {
                      const instanceId = Number(instance?.id);
                      const label =
                        instance?.workflowContainerName ||
                        instance?.name ||
                        String(instance?.id ?? "-");
                      const isSelected =
                        !Number.isNaN(instanceId) &&
                        selectedWorkflowInstanceIds.includes(instanceId);
                      return (
                        <button
                          key={String(instance?.id)}
                          type="button"
                          onClick={() => {
                            if (!Number.isNaN(instanceId)) {
                              toggleWorkflowInstanceSelection(instanceId);
                            }
                          }}
                          disabled={Number.isNaN(instanceId)}
                          className={`flex items-center gap-3 border rounded-xl px-4 py-3 text-left transition-colors ${
                            isSelected
                              ? "border-blue-300 bg-blue-50"
                              : "border-slate-200 hover:bg-slate-50"
                          }`}
                        >
                          <div className="relative">
                            <div className="h-10 w-10 rounded-full bg-slate-100 flex items-center justify-center text-slate-500 text-xs font-semibold">
                              {label
                                .split(" ")
                                .filter(Boolean)
                                .slice(0, 2)
                                .map((part: string) => part[0]?.toUpperCase())
                                .join("") || "#"}
                            </div>
                            {isSelected && (
                              <CheckCircleIcon className="h-4 w-4 text-blue-600 absolute -right-1 -bottom-1 bg-white rounded-full" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-semibold text-slate-900 truncate">
                              {label}
                            </div>
                            <div className="text-xs text-slate-500 truncate">
                              #{String(instance?.id ?? "-")}
                            </div>
                          </div>
                          <input
                            type="checkbox"
                            checked={isSelected}
                            readOnly
                            onClick={(event) => {
                              event.stopPropagation();
                              if (!Number.isNaN(instanceId)) {
                                toggleWorkflowInstanceSelection(instanceId);
                              }
                            }}
                            className="h-4 w-4 text-blue-600 border-slate-300 rounded"
                          />
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              <label className="mt-5 flex items-start gap-3 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
                <input
                  type="checkbox"
                  checked={visitorFinancialAccess}
                  onChange={(e) => setVisitorFinancialAccess(e.target.checked)}
                  className="mt-1 h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                />
                <span>
                  <span className="font-semibold text-slate-900">
                    {t(
                      "users:visitorFinancialAccess",
                      "Fatura ve teklif bilgilerini görüntüleyebilir"
                    )}
                  </span>
                  <br />
                  <span className="text-slate-500">
                    {t(
                      "users:visitorFinancialAccessDescription",
                      "İşaretlenirse ziyaretçi fatura/teklif dosyalarını ve iş tutarı-teklif miktarını görebilir."
                    )}
                  </span>
                </span>
              </label>
            </div>

            <div className="p-6 border-t border-slate-200 flex justify-end gap-3 bg-white">
              <button
                type="button"
                onClick={closeVisitorModal}
                className="px-4 py-2 text-sm border border-slate-200 rounded-lg text-slate-700 hover:bg-slate-50"
              >
                {t("common:cancel")}
              </button>
              <button
                type="button"
                onClick={handleVisitorSelectionSave}
                disabled={updating === targetUser.id}
                className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-60"
              >
                {updating === targetUser.id ? t("common:loading") : t("common:save")}
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        isOpen={!!userToDelete}
        onClose={() => setUserToDelete(null)}
        onConfirm={confirmDeleteUser}
        title={t("users:deleteUser")}
        message={t("users:confirmDelete")}
        confirmText={t("common:delete")}
        cancelText={t("common:cancel")}
        type="danger"
      />
      <ConfirmDialog
        isOpen={!!userToPromote}
        onClose={() => setUserToPromote(null)}
        onConfirm={confirmPromoteToManager}
        title={t("users:promoteToManager", "Yönetici Yap")}
        message={t(
          "users:confirmPromoteToManager",
          "Bu kullanıcı yönetici yetkisi alacak. Devam etmek istediğinizden emin misiniz?"
        )}
        confirmText={t("common:confirm", "Onayla")}
        cancelText={t("common:cancel")}
        type="warning"
      />
    </div>
  );
};
